// constants.js

export const FIELD_TYPES = [
    'string',
    'richtext',
    'signature',
    'barcode',
    'phone',
    'email',
    'measurement',
    'enum',
    'pipeline',
    'multiselect',
    'people',
    'relation',
    'annualdate',
    'date',
    'daterange',
    'datetime',
    'datetimerange',
    'cover',
    'color',
    'subtitle',
    'location',
    'boolean',
    'number',
    'sequence',
    'percentage',
    'money',
    'currency',
    'url',
    'checklist',
    'tags',
    'comments',
    'activity',
];

export const ONCE_ONLY_FIELD_TYPES = [
    'cover',
    'subtitle',
    'comments',
    'activity'
];

export const FIELD_TYPE_LABELS = {
    cover: 'Cover Photo',
    color: 'Color',
    subtitle: 'Subtitle',
    barcode: 'Bar Code',
    string: 'Text',
    text: 'Long Text',
    phone: 'Phone Number',
    email: 'Email',
    measurement: 'Measurement',
    annualdate: 'Annual Date',
    date: 'Date',
    daterange: 'Date Range',
    datetime: 'Date and Time',
    datetimerange: 'Date and Time Range',
    enum: 'Select',
    pipeline: 'Pipeline',
    multiselect: 'Multi-Select',
    location: 'Location',
    boolean: 'True / False',
    number: 'Number',
    sequence: 'Sequence',
    percentage: 'Percentage',
    money: 'Money',
    currency: 'Currency',
    relation: 'Relation',
    url: 'URL',
    people: 'People',
    checklist: 'Checklist',
    tags: 'Tags',
    comments: 'Comments',
    activity: 'Activity',
    richtext: 'Rich Text',
    signature: 'Signature',
};

export const FIELD_TYPE_DESCRIPTIONS = {
    barcode: 'A barcode value with manual entry, paste, and camera scanning.',
    string: 'Plain text for names, titles, or short answers.',
    text: 'Longer plain text for notes or multi-line answers.',
    phone: 'A phone number with US-first formatting.',
    email: 'An email address with lowercase canonical storage.',
    measurement: 'A decimal value paired with a unit selected from your list.',
    boolean: 'A yes or no value.',
    number: 'A numeric value.',
    sequence: 'An auto-generated business number (for example PO-10042) that can be edited after create. Pattern and next value are set in base Settings → Sequences.',
    enum: 'Choose a single option from a predefined list.',
    pipeline: 'A stage in a process pipeline. Board-groupable like Select; later supports stage duration targets.',
    multiselect: 'Choose one or more options from a predefined list.',
    people: 'Reference one or more people.',
    relation: 'Relate one or more records of another type.',
    annualdate: 'Useful for recurring dates, like birthdays or holidays.',
    date: 'A specific calendar date without a time.',
    daterange: 'A specific start and end date.',
    datetime: 'A specific date and time.',
    datetimerange: 'A specific start and end date with times.',
    cover: 'Main cover image for this item type.',
    color: 'A color swatch for accents in lists, calendars, and project views.',
    subtitle: 'Single subtitle line shown in lists, cards, and other previews.',
    richtext: 'Formatted body text with headings, emphasis, and embedded content blocks.',
    signature: 'A named acknowledgement — typed name or drawn mark — with timestamp.',
    location: 'Place or address details.',
    percentage: 'A numeric percentage with optional snap increments.',
    money: 'A monetary amount in a selected currency.',
    currency: 'An ISO currency code (for example USD or EUR) for this record.',
    url: 'A website or external link.',
    checklist: 'A named checklist on each record. Add template items for a shared list, or leave empty for a freeform per-record list.',
    tags: 'One or more lightweight labels.',
    comments: 'Built-in discussion thread for the item.',
    activity: 'Built-in activity feed for changes and events.',
};

/** Material icon keys for field-type pickers (SelectInput, field rows). */
export const FIELD_TYPE_ICON_KEYS = {
    activity: 'timeline',
    annualdate: 'event',
    barcode: 'barcode',
    boolean: 'toggle_on',
    checklist: 'checklist',
    color: 'palette',
    comments: 'chat',
    cover: 'image',
    money: 'attach_money',
    currency: 'payments',
    date: 'calendar_today',
    daterange: 'date_range',
    datetime: 'schedule',
    datetimerange: 'event_repeat',
    email: 'mail',
    enum: 'list_alt',
    pipeline: 'view_timeline',
    location: 'location_on',
    measurement: 'straighten',
    multiselect: 'library_add_check',
    relation: 'format_list_bulleted',
    number: 'tag',
    sequence: 'tag',
    people: 'group',
    percentage: 'percent',
    phone: 'call',
    richtext: 'article',
    signature: 'draw',
    string: 'title',
    subtitle: 'description',
    tags: 'sell',
    text: 'short_text',
    url: 'link',
};

export const DEFAULT_LABELS_BY_TYPE = {
    cover: 'Cover Photo',
    color: 'Color',
    subtitle: 'Subtitle',
    barcode: 'Bar Code',
    annualdate: 'Annual Date',
    date: 'Date',
    daterange: 'Date Range',
    datetime: 'Date and Time',
    datetimerange: 'Date and Time Range',
    location: 'Location',
    phone: 'Phone Number',
    email: 'Email',
    url: 'Website',
    people: 'People',
    relation: 'Relation',
    pipeline: 'Pipeline Stage',
    checklist: 'Checklist',
    tags: 'Tags',
    comments: 'Comments',
    activity: 'Activity',
    currency: 'Currency',
    money: 'Money',
    signature: 'Signature',
};

export const DEFAULT_VALUE_UNSUPPORTED_TYPES = [
    'cover',
    'color',
    'location',
    'annualdate',
    'date',
    'daterange',
    'datetime',
    'datetimerange',
    'people',
    'relation',
    'checklist',
    'tags',
    'multiselect',
    'measurement',
    'richtext',
    'signature',
    'sequence',
    'url',
    'comments',
    'activity'
];

export const ENUM_OPTIONS_SORT_MODE_MANUAL = 'manual';
export const ENUM_OPTIONS_SORT_MODE_LABEL_ASC = 'label_asc';
export const ENUM_OPTIONS_SORT_MODE_LABEL_DESC = 'label_desc';

export const ENUM_OPTIONS_SORT_MODE_OPTIONS = [
    { value: ENUM_OPTIONS_SORT_MODE_MANUAL, label: 'Manual (original order)' },
    { value: ENUM_OPTIONS_SORT_MODE_LABEL_ASC, label: 'Label (A to Z)' },
    { value: ENUM_OPTIONS_SORT_MODE_LABEL_DESC, label: 'Label (Z to A)' },
];

/** Re-export shared ISO list for field-modal money settings. */
export { CURRENCY_CODE_OPTIONS } from 'utilities/Currency';

export const makeDraft = () => ({
    label: '',
    fieldDescription: '',
    type: 'string',
    targetType: '',
    allowMultiple: true,
    showFilter: false,
    displayFields: [],
    showAccess: false,
    showAsProfileStack: false,
    includeInQrPrint: false,
    displayInDefaultLists: false,
    checklistItems: [],
    allowCustomItems: false,
    default: '',
    defaultUnit: '',
    currencyCode: 'USD',
    options: [],
    enumOptionsSort: ENUM_OPTIONS_SORT_MODE_MANUAL,
    calendarColor: '',
});
